import { AnimatePresence, motion } from "framer-motion";
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { drawerPresence } from "./motion";

export type DrawerProps = {
  open: boolean;
  title?: ReactNode;
  children: ReactNode;
  onClose?: () => void;
  /** Accessible label when `title` is not a string. */
  ariaLabel?: string;
};

/**
 * Off-canvas panel sliding in from the left edge — Modal's sibling for
 * navigation content that scales vertically (a growing list) rather than a
 * single focused action. Same Escape/backdrop-click-to-close contract as
 * Modal; callers close it explicitly on selection (e.g. a nav link's
 * onClick) rather than relying on click-bubbling, since a click inside the
 * drawer is normally "pick this and go", not "dismiss".
 *
 * Rendered via createPortal(..., document.body) — not inline like Modal.
 * `position: fixed` is only viewport-relative when NO ancestor sets a
 * transform/filter/perspective/will-change (any of those makes that
 * ancestor the containing block instead — a real, easy-to-hit CSS trap for
 * an element mounted deep inside a caller's own flex/animated tree, e.g. a
 * ProjectSidebar nested inside a flex layout next to Framer Motion page
 * transitions). Portaling to <body> makes the containing block always the
 * viewport, regardless of where the component that renders <Drawer> lives.
 */
export function Drawer({ open, title, children, onClose, ariaLabel }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="ax-drawer-backdrop"
          className="ax-drawer-backdrop"
          role="presentation"
          initial={drawerPresence.backdrop.initial}
          animate={drawerPresence.backdrop.animate}
          exit={drawerPresence.backdrop.exit}
          transition={drawerPresence.backdrop.transition}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            className="ax-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel ?? (typeof title === "string" ? title : "Panel")}
            initial={drawerPresence.panel.initial}
            animate={drawerPresence.panel.animate}
            exit={drawerPresence.panel.exit}
            transition={drawerPresence.panel.transition}
          >
            {title != null ? <h2 className="ax-drawer__title">{title}</h2> : null}
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
